<template>
  <div>
    <v-card class="detail-card d-flex flex-column" style="height:100vh;">
      <!-- Toolbar -->
      <div class="dlg-toolbar flex-shrink-0 d-flex align-center px-3">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>fas fa-times</v-icon>
        </v-btn>
        <v-avatar color="success" size="36" class="ml-2 mr-3">
          <v-icon small color="white">fas fa-file-invoice</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-1 font-weight-black white--text lh-1">Detalle Documento — Softnet</div>
          <div v-if="softnet_data.idDoc" class="caption success--text lh-1 mt-1">
            Folio #{{ softnet_data.idDoc.folio }} · Sincronizado
          </div>
        </div>
        <v-spacer />
      </div>

      <!-- Body -->
      <div class="detail-body flex-grow-1 overflow-y-auto pa-6">
        <!-- Loading -->
        <div v-if="loading" class="d-flex flex-column align-center justify-center" style="min-height:300px;">
          <v-progress-circular indeterminate color="success" size="52" />
          <div class="caption grey--text mt-5">Cargando datos desde Softnet...</div>
        </div>

        <div v-else-if="softnet_data.idDoc">
          <v-row>
            <!-- Columna izquierda: datos del documento -->
            <v-col cols="12" md="8">
              <!-- Encabezado del documento -->
              <v-card outlined class="modern-card mb-5">
                <div class="doc-header d-flex justify-space-between align-start pa-6">
                  <div>
                    <div class="text-h6 font-weight-black primary--text">{{ softnet_data.receptor.RznSocRecep || '—' }}</div>
                    <div class="caption grey--text mt-1">{{ softnet_data.receptor.GiroRecep || '—' }}</div>
                    <div class="caption grey--text">RUT: {{ softnet_data.receptor.RUTRecep || '—' }}</div>
                  </div>
                  <div class="text-right">
                    <div class="doc-badge">
                      <div class="doc-badge-type">NOTA DE VENTA</div>
                      <div class="doc-badge-folio">N° {{ softnet_data.idDoc.folio }}</div>
                      <div class="mt-2">
                        <v-chip small color="success" class="font-weight-black">
                          <v-icon left x-small>fas fa-check-circle</v-icon>
                          Sincronizado
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card>

              <!-- Tabla de productos -->
              <v-card outlined class="modern-card mb-5">
                <div class="px-6 pt-5 pb-2">
                  <div class="section-title mb-4">
                    <v-icon x-small color="success" class="mr-2">fas fa-list</v-icon>
                    LÍNEAS DE DETALLE
                  </div>
                  <table class="detail-table">
                    <thead>
                      <tr>
                        <th class="text-left">Código</th>
                        <th class="text-left">Descripción</th>
                        <th class="text-center">Cant.</th>
                        <th class="text-right">P. Unit. Neto</th>
                        <th class="text-right">Total Neto</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, i) in softnet_data.Detalle"
                        :key="i"
                        :class="i % 2 === 0 ? 'row-even' : 'row-odd'"
                      >
                        <td class="td-sku">{{ item.VlrCodigo || '—' }}</td>
                        <td class="td-name">{{ item.NmbItem }}</td>
                        <td class="text-center td-qty">{{ item.QtyItem }}</td>
                        <td class="text-right td-price">{{ item.PrcItem | moneda }}</td>
                        <td class="text-right td-total font-weight-bold">{{ item.MontoItem | moneda }}</td>
                      </tr>
                      <tr v-if="!softnet_data.Detalle || softnet_data.Detalle.length === 0">
                        <td colspan="5" class="text-center grey--text caption py-6">Sin líneas de detalle</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Totales -->
                <div class="d-flex justify-end pa-6 pt-3">
                  <div class="totals-box">
                    <div class="totals-row">
                      <span class="totals-label">Monto Neto</span>
                      <span class="totals-amount">{{ softnet_data.totales.MntNeto | moneda }}</span>
                    </div>
                    <div class="totals-row">
                      <span class="totals-label">IVA ({{ softnet_data.totales.TasaIVA }}%)</span>
                      <span class="totals-amount">{{ softnet_data.totales.IVA | moneda }}</span>
                    </div>
                    <v-divider class="my-2" />
                    <div class="totals-row totals-grand">
                      <span class="totals-label-grand">TOTAL</span>
                      <span class="totals-amount-grand">{{ softnet_data.totales.MntTotal | moneda }}</span>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>

            <!-- Columna derecha: metadatos -->
            <v-col cols="12" md="4">
              <!-- Receptor -->
              <v-card outlined class="modern-card mb-5 pa-5">
                <div class="section-title mb-4">
                  <v-icon x-small color="success" class="mr-2">fas fa-user-tie</v-icon>
                  RECEPTOR
                </div>
                <div class="meta-label">RUT</div>
                <div class="meta-value">{{ softnet_data.receptor.RUTRecep || '—' }}</div>
                <div class="meta-label mt-3">Razón Social</div>
                <div class="meta-value">{{ softnet_data.receptor.RznSocRecep || '—' }}</div>
                <div class="meta-label mt-3">Giro</div>
                <div class="meta-value">{{ softnet_data.receptor.GiroRecep || '—' }}</div>
              </v-card>

              <!-- Datos de emisión -->
              <v-card outlined class="modern-card mb-5 pa-5">
                <div class="section-title mb-4">
                  <v-icon x-small color="success" class="mr-2">fas fa-calendar-alt</v-icon>
                  DATOS DE EMISIÓN
                </div>
                <div class="meta-label">Fecha Emisión ERP</div>
                <div class="meta-value">{{ softnet_data.idDoc.fechaEmis }}</div>
                <div class="meta-label mt-3">Registrado por</div>
                <div class="meta-value">{{ created_by }}</div>
              </v-card>

              <!-- Footer Softnet ID -->
              <div class="mt-4 caption grey--text text-center">
                <v-icon x-small class="mr-1 success--text">fas fa-check-circle</v-icon>
                Sincronizado con <strong>Softnet</strong>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { getDocument } from "@/api/softnet";
import notifications from "@/utils/notifications";

export default {
  name: 'DocumentDetail',
  props: ['document_id'],
  data() {
    return {
      loading: false,
      softnet_data: {
        idDoc: {},
        receptor: {},
        totales: {},
        Detalle: []
      },
      created_by: '',
    }
  },
  methods: {
    async fetchSoftnetData() {
      if (!this.document_id) return;
      this.loading = true;
      try {
        const response = await getDocument(this.document_id);
        this.softnet_data = response.softnet_data;
        this.created_by = response.created_by;
      } catch (error) {
        notifications.showError('Error al recuperar datos de Softnet');
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    document_id: {
      immediate: true,
      handler: 'fetchSoftnetData'
    }
  }
}
</script>

<style scoped>
.dlg-toolbar { height: 64px; min-height: 64px; max-height: 64px; background-color: #1A1F2E; }
.detail-card { background-color: #f4f7f9; border-radius: 0; }
.detail-body { background-color: #f4f7f9; }

.modern-card {
  border-radius: 10px !important;
  border: 1px solid rgba(0,0,0,0.06) !important;
  background: white !important;
}

.doc-header { background: linear-gradient(135deg, #f8fafc 0%, #eef6f7 100%); border-radius: 10px; }

.doc-badge {
  background: #1A1F2E;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  text-align: center;
  min-width: 180px;
}
.doc-badge-type {
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  opacity: 0.8;
}
.doc-badge-folio {
  font-size: 1.3rem;
  font-weight: 900;
  color: #4CAF50;
  margin-top: 4px;
}

.section-title {
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 2px;
  color: #4CAF50;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

.meta-label {
  font-size: 0.68rem;
  color: #90a4ae;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.meta-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1A1F2E;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.83rem;
  margin-bottom: 4px;
}
.detail-table thead tr { background: #f0f4f7; }
.detail-table th {
  padding: 8px 10px;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #546e7a;
  border-bottom: 2px solid #e0e7ef;
}
.detail-table td { padding: 9px 10px; border-bottom: 1px solid #f0f4f7; vertical-align: middle; }
.row-even { background: white; }
.row-odd  { background: #fafbfc; }
.td-sku   { font-weight: 700; color: #4CAF50; font-size: 0.78rem; }
.td-name  { color: #1A1F2E; }
.td-qty   { color: #607d8b; }
.td-price { color: #607d8b; font-variant-numeric: tabular-nums; }
.td-total { color: #1A1F2E; font-variant-numeric: tabular-nums; }

.totals-box { min-width: 260px; }
.totals-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }
.totals-label { font-size: 0.82rem; color: #607d8b; font-weight: 600; }
.totals-amount { font-size: 0.9rem; font-weight: 700; color: #1A1F2E; font-variant-numeric: tabular-nums; }
.totals-grand { padding-top: 6px; }
.totals-label-grand { font-size: 0.9rem; font-weight: 900; color: #1A1F2E; letter-spacing: 1px; text-transform: uppercase; }
.totals-amount-grand { font-size: 1.5rem; font-weight: 900; color: #1A1F2E; font-variant-numeric: tabular-nums; letter-spacing: -0.5px; }

.lh-1 { line-height: 1 !important; }
</style>